import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { forwardRef, useContext, useEffect, useState } from 'react';
import CustomSelect2 from './CustomSelect2';
import { Box, Typography, FormControlLabel, Grid, TextField, InputAdornment, Select, MenuItem, colors, Button, Skeleton } from '@mui/material';
import SelectPlanCheckbox from './SelectPlanCheckbox';
import { ConfigContext } from '../utils/ConfigContext';
import TableRatesRow from './TableRatesRow';
import getSafe from '../utils/getSafe';
import domtoimage from 'dom-to-image';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PercentInput from './PercentInput';

const AllianzCareSingapore_Rates = forwardRef(({ appConfig, sx, clients, loadingStatus, currency, selectedPlans, setSelectedPlans,
  response, deductibles, setDeductibles, addToPendingTasks,
  discounts, setDiscounts, computedRates, setComputedRates,
  specificAreaOfCoverage, setSpecificAreaOfCoverage, areaOfCoverage,
  setShowImageCopy, selectedOptions, addSelectedOption,
  setViewSelectedOption, specificCurrency, setSpecificCurrency,
  specificPaymentFrequency, setSpecificPaymentFrequency,
  allianzCareSingaporeSpecific, setAllianzCareSingaporeSpecific, loadings, setLoadings }, ref) => {
  // const { appConfig } = useContext(ConfigContext);

  const id = 'allianz_care_singapore';

  const inpatientOptions = getSafe(() => appConfig.content.specifics.allianz_care_singapore.inpatient_options);
  const outpatientOptions = getSafe(() => appConfig.content.specifics.allianz_care_singapore.outpatient_options);
  const maternityOptions = getSafe(() => appConfig.content.specifics.allianz_care_singapore.maternity_options);
  const dentalOptions = getSafe(() => appConfig.content.specifics.allianz_care_singapore.dental_options);
  const repatriationOptions = getSafe(() => appConfig.content.specifics.allianz_care_singapore.repatriation_options);
  const copaymentOptions = getSafe(() => appConfig.content.specifics.allianz_care_singapore.copayment_options);

  const gst = getSafe(() => appConfig.content.specifics.allianz_care_singapore.gst_rate, 0);


  const [inpatientOption, setInpatientOption] = useState(inpatientOptions[0]);
  const [outpatientOption, setOutpatientOption] = useState(outpatientOptions[inpatientOption][0]);
  const [maternityOption, setMaternityOption] = useState(maternityOptions[inpatientOption][0]);
  const [dentalOption, setDentalOption] = useState(dentalOptions[inpatientOption][0]);
  const [repatriationOption, setRepatriationOption] = useState(repatriationOptions[0]);
  const [copaymentOption, setCopaymentOption] = useState(copaymentOptions[0]);
  const [family, setFamily] = useState('Yes');

  const [validationErrorMessage, setValidationErrorMessage] = useState('');

  const specific_area_of_coverages = getSafe(() => appConfig.content.set2_area_of_coverage);

  useEffect(() => {
    if (outpatientOptions[inpatientOption].indexOf(outpatientOption) === -1) {
      setOutpatientOption(outpatientOptions[inpatientOption][0]);
    }

    if (maternityOptions[inpatientOption].indexOf(maternityOption) === -1) {
      setMaternityOption(maternityOptions[inpatientOption][0]);
    }

    if (dentalOptions[inpatientOption].indexOf(dentalOption) === -1) {
      setDentalOption(dentalOptions[inpatientOption][0]);
    }
  }, [inpatientOption]);

  useEffect(() => {
    // count adults in clients
    const adults = clients.filter(client => client.age > 18).length;

    if (adults < 2 && maternityOption !== 'No Maternity') {
      setValidationErrorMessage('Maternity is only available with 2 or more adults.');
    } else {
      setValidationErrorMessage('');
    }
  }, [response]);

  useEffect(() => {
    allianzCareSingaporeSpecific['inpatient_plan'] = inpatientOption;
    allianzCareSingaporeSpecific['outpatient_plan'] = outpatientOption;
    allianzCareSingaporeSpecific['maternity_plan'] = maternityOption;
    allianzCareSingaporeSpecific['dental_plan'] = dentalOption;
    allianzCareSingaporeSpecific['repatriation_plan'] = repatriationOption;
    allianzCareSingaporeSpecific['outpatient_co_payment'] = copaymentOption;
    allianzCareSingaporeSpecific['family'] = family;

    setAllianzCareSingaporeSpecific({ ...allianzCareSingaporeSpecific });
  }, []);

  useEffect(() => {
    computedRates[id] = getComputedRates();
    setComputedRates(computedRates);
  }, [response, discounts, loadings, inpatientOption, outpatientOption, maternityOption, dentalOption, repatriationOption, copaymentOption, family]);

  useEffect(() => {
    setTimeout(() => {

      // inpatient_plan, outpatient_plan, maternity_plan, dental_plan, repatriation_plan
      // copayment, family
      addSelectedOption(id, 'inpatient_plan', inpatientOption);
      addSelectedOption(id, 'outpatient_plan', outpatientOption);
      addSelectedOption(id, 'maternity_plan', maternityOption);
      addSelectedOption(id, 'dental_plan', dentalOption);
      addSelectedOption(id, 'repatriation_plan', repatriationOption);
      addSelectedOption(id, 'outpatient_co_payment', copaymentOption);
      addSelectedOption(id, 'family', family);

      addSelectedOption(id, 'specific_area_of_coverage', specificAreaOfCoverage[id]);
      addSelectedOption(id, 'specific_currency', specificCurrency[id]);
      addSelectedOption(id, 'specific_payment_frequency', specificPaymentFrequency[id]);
      addSelectedOption(id, 'deductible', deductibles[id]);
      addSelectedOption(id, 'discount', discounts[id] == '' ? 0 : discounts[id]);
      addSelectedOption(id, 'loading', loadings[id] == '' ? 0 : loadings[id]);
    }, 1000);
  }, [specificAreaOfCoverage[id], specificCurrency[id], specificPaymentFrequency[id], deductibles[id], discounts[id], loadings[id],
    inpatientOption, outpatientOption, maternityOption, dentalOption, repatriationOption, copaymentOption, family]);

  useEffect(() => {

    // inpatient_plan, outpatient_plan, maternity_plan, dental_plan, repatriation_plan
    // copayment, family
    setViewSelectedOption(id, 'inpatient_plan', setInpatientOption);
    setViewSelectedOption(id, 'outpatient_plan', setOutpatientOption);
    setViewSelectedOption(id, 'maternity_plan', setMaternityOption);
    setViewSelectedOption(id, 'dental_plan', setDentalOption);
    setViewSelectedOption(id, 'repatriation_plan', setRepatriationOption);
    setViewSelectedOption(id, 'outpatient_co_payment', setCopaymentOption);
    setViewSelectedOption(id, 'family', setFamily);

    // set allianzCareSingaporeSpecific
    

    setViewSelectedOption(id, 'inpatient_plan', (option_value) => {
      allianzCareSingaporeSpecific['inpatient_plan'] = option_value;
      setAllianzCareSingaporeSpecific({ ...allianzCareSingaporeSpecific });
    });

    setViewSelectedOption(id, 'outpatient_plan', (option_value) => {
      allianzCareSingaporeSpecific['outpatient_plan'] = option_value;
      setAllianzCareSingaporeSpecific({ ...allianzCareSingaporeSpecific });
    });

    setViewSelectedOption(id, 'maternity_plan', (option_value) => {
      allianzCareSingaporeSpecific['maternity_plan'] = option_value;
      setAllianzCareSingaporeSpecific({ ...allianzCareSingaporeSpecific });
    });

    setViewSelectedOption(id, 'dental_plan', (option_value) => {
      allianzCareSingaporeSpecific['dental_plan'] = option_value;
      setAllianzCareSingaporeSpecific({ ...allianzCareSingaporeSpecific });
    });

    setViewSelectedOption(id, 'repatriation_plan', (option_value) => {
      allianzCareSingaporeSpecific['repatriation_plan'] = option_value;
      setAllianzCareSingaporeSpecific({ ...allianzCareSingaporeSpecific });
    });

    setViewSelectedOption(id, 'outpatient_co_payment', (option_value) => {
      allianzCareSingaporeSpecific['outpatient_co_payment'] = option_value;
      setAllianzCareSingaporeSpecific({ ...allianzCareSingaporeSpecific });
    });

    setViewSelectedOption(id, 'family', (option_value) => {
      allianzCareSingaporeSpecific['family'] = option_value;
      setAllianzCareSingaporeSpecific({ ...allianzCareSingaporeSpecific });
    });


    setViewSelectedOption(id, 'deductible', (option_value) => {
      deductibles[id] = option_value;
      setDeductibles(deductibles);
    });
    setViewSelectedOption(id, 'discount', (option_value) => {
      discounts[id] = option_value;
      setDiscounts(discounts);
    });

    setViewSelectedOption(id, 'loading', (option_value) => {
      loadings[id] = option_value;
      setLoadings(loadings);
    });

    setViewSelectedOption(id, 'specific_area_of_coverage', (option_value) => {
      specificAreaOfCoverage[id] = option_value;
      setSpecificAreaOfCoverage(specificAreaOfCoverage);
    });

    setViewSelectedOption(id, 'specific_currency', (option_value) => {
      specificCurrency[id] = option_value;
      setSpecificCurrency(specificCurrency);
    });

    setViewSelectedOption(id, 'specific_payment_frequency', (option_value) => {
      specificPaymentFrequency[id] = option_value;
      setSpecificPaymentFrequency(specificPaymentFrequency);
    });

    
  }, [selectedOptions]);

  function getComputedRates() {
    if (!response) return;

    const rates = response.rates;
    const discount = parseFloat(discounts[id]) || 0;
    const loading = parseFloat(loadings[id]) || 0;
    const totalPercent = ((100 - discount + loading) * 0.01);

    const computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      const inpatient = computedRate(rates[i].inpatient);
      const outpatient = computedRate(rates[i].outpatient);
      const maternity = computedRate(rates[i].maternity);
      const dental = computedRate(rates[i].dental);
      const repatriation = computedRate(rates[i].repatriation);

      computedRates.push({
        'client': rates[i].client,
        'currency': rates[i].currency,
        'inpatient': inpatient * totalPercent,
        'outpatient': outpatient * totalPercent,
        'maternity': maternity * totalPercent,
        'dental': dental * totalPercent,
        'repatriation': repatriation * totalPercent,
        'family_discount': rates[i].family_discount,
      })
    }

    return computedRates;
  }


  function computeAllianzCareSingaporeRates(rates) {
    const computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      computedRates.push({
        'client': rates[i].client,
        'currency': rates[i].currency,
        'inpatient': computedRate(rates[i].inpatient),
        'outpatient': computedRate(rates[i].outpatient),
        'maternity': computedRate(rates[i].maternity),
        'dental': computedRate(rates[i].dental),
        'repatriation': computedRate(rates[i].repatriation),
      })
    }

    return computedRates;
  }

  function computedRate(rate) {
    if (rate === 'Unavailable' || typeof rate === 'string') {
      return rate;
    }


    let total = rate;

    return total;
  }

  function computeTotalPremium() {
    let total = 0;

    for (let i = 0; i < clients.length; i++) {
      const totalPerClient = computeTotalPremiumPerClient(i);
      if (!isNaN(totalPerClient)) {
        total += totalPerClient;
      }
    }

    return total;
  }

  function computeTotalPremiumPerClient(clientIndex) {
    const computedRates = getComputedRates();

    if (!computedRates) return 0;

    const rates = computedRates[clientIndex];

    if (!rates) return 0;

    let total = 0;

    if (rates['inpatient'] !== 'Unavailable' && !isNaN(rates['inpatient'])) {
      total += rates['inpatient'];
    }

    if (rates['outpatient'] !== 'Unavailable' && !isNaN(rates['outpatient'])) {
      total += rates['outpatient'];
    }

    if (rates['maternity'] !== 'Unavailable' && !isNaN(rates['maternity'])) {
      total += rates['maternity'];
    }

    if (rates['dental'] !== 'Unavailable' && !isNaN(rates['dental'])) {
      total += rates['dental'];
    }

    if (rates['repatriation'] !== 'Unavailable' && !isNaN(rates['repatriation'])) {
      total += rates['repatriation'];
    }


    return total;
  }

  function checkIfFamilyDiscountPerClient(clientIndex) {
    const computedRates = getComputedRates();

    if (!computedRates) return 'No';

    const rates = computedRates[clientIndex];

    if (!rates) return 'No';

    return rates['family_discount'];
  }

  function formatRate(rate) {
    if (isNaN(rate) || rate == 'Unavailable' || typeof rate === 'string') {
      return <span style={{ color: '#f44336' }}>{'Unavailable'}</span>
    }

    if (isNaN(rate)) {
      return rate;
    }

    return getCurrencySign() + rate.toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  function getCurrencySign() {
    const currency = getCurrency();

    if (currency === 'USD') {
      return '$';
    } else if (currency === 'EURO') {
      return '€';
    } else if (currency === 'GBP') {
      return '£';
    } else if (currency === 'SGD') {
      return 'S$';
    }

    return '';
  }

  function getCurrency() {
    const computedRates = getComputedRates();

    if (!computedRates) return '';

    if (computedRates.length === 0) { return '' }

    return computedRates[0].currency;
  }

  return (
    <Box id={id} sx={sx}>
      <Paper sx={{ borderColor: '#e0e0e0', borderWidth: 2, pb: 2 }} variant='outlined' >
        <Typography variant='h5' sx={{ color: '#fff', background: '#003781', px: 2, py: 1 }}>Allianz Care Singapore</Typography>

        <Box sx={{ p: 2 }}>

          <Box sx={{ display: 'flex', gap: 5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={18} fontWeight={600}>Plan Type</Typography>
              <Box sx={{ display: 'flex', alignItems: 'start', mt: 2 }}>
                <Typography fontSize={18} fontWeight={400} sx={{ width: 140 }}>Inpatient</Typography>
                <Select
                  size='small'
                  value={inpatientOption}
                  onChange={(e) => {
                    setInpatientOption(e.target.value);

                    allianzCareSingaporeSpecific['inpatient_plan'] = e.target.value;
                    setAllianzCareSingaporeSpecific({ ...allianzCareSingaporeSpecific });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    inpatientOptions && inpatientOptions.map(inpatientOption => (
                      <MenuItem key={inpatientOption} value={inpatientOption}>{inpatientOption}</MenuItem>
                    ))
                  }

                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'start', mt: 2 }}>
                <Typography fontSize={18} fontWeight={400} sx={{ width: 140 }}>Outpatient</Typography>
                <Select
                  size='small'
                  value={outpatientOption}
                  onChange={(e) => {
                    setOutpatientOption(e.target.value);

                    allianzCareSingaporeSpecific['outpatient_plan'] = e.target.value;
                    setAllianzCareSingaporeSpecific({ ...allianzCareSingaporeSpecific });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    outpatientOptions[inpatientOption] && outpatientOptions[inpatientOption].map(outpatientOption => (
                      <MenuItem key={outpatientOption} value={outpatientOption}>{outpatientOption}</MenuItem>
                    ))
                  }

                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'start', mt: 2 }}>
                <Typography fontSize={18} fontWeight={400} sx={{ width: 140 }}>Maternity</Typography>
                <Select
                  size='small'
                  value={maternityOption}
                  onChange={(e) => {
                    setMaternityOption(e.target.value);

                    allianzCareSingaporeSpecific['maternity_plan'] = e.target.value;
                    setAllianzCareSingaporeSpecific({ ...allianzCareSingaporeSpecific });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    maternityOptions[inpatientOption] && maternityOptions[inpatientOption].map(maternityOption => (
                      <MenuItem key={maternityOption} value={maternityOption}>{maternityOption}</MenuItem>
                    ))
                  }

                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'start', mt: 2 }}>
                <Typography fontSize={18} fontWeight={400} sx={{ width: 140 }}>Dental</Typography>
                <Select
                  size='small'
                  value={dentalOption}
                  onChange={(e) => {
                    setDentalOption(e.target.value);

                    allianzCareSingaporeSpecific['dental_plan'] = e.target.value;
                    setAllianzCareSingaporeSpecific({ ...allianzCareSingaporeSpecific });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    dentalOptions[inpatientOption] && dentalOptions[inpatientOption].map(dentalOption => (
                      <MenuItem key={dentalOption} value={dentalOption}>{dentalOption}</MenuItem>
                    ))
                  }

                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'start', mt: 2 }}>
                <Typography fontSize={18} fontWeight={400} sx={{ width: 140 }}>Repatriation</Typography>
                <Select
                  size='small'
                  value={repatriationOption}
                  onChange={(e) => {
                    setRepatriationOption(e.target.value);

                    allianzCareSingaporeSpecific['repatriation_plan'] = e.target.value;
                    setAllianzCareSingaporeSpecific({ ...allianzCareSingaporeSpecific });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    repatriationOptions && repatriationOptions.map(repatriationOption => (
                      <MenuItem key={repatriationOption} value={repatriationOption}>{repatriationOption}</MenuItem>
                    ))
                  }

                </Select>
              </Box>


            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography fontSize={18} fontWeight={400} sx={{ visibility: 'hidden' }}>Options</Typography>

              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={400} sx={{ width: 200 }}>Deductible</Typography>
                <CustomSelect2
                  sx={{ minWidth: 190 }}
                  value={deductibles[id]}
                  onChange={(e) => {

                    const val = e.target.value;

                    const _deductibles = JSON.parse(JSON.stringify(deductibles));

                    _deductibles[id] = val;

                    setDeductibles(_deductibles);

                    addToPendingTasks([id]);

                  }}
                  items={getSafe(() => appConfig.content.deductibles.allianz_care_singapore[specificCurrency[id]], [])}
                  disabled={loadingStatus[id]}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={400} sx={{ width: 200 }}>Copay</Typography>
                <Select
                  size='small'
                  value={copaymentOption}
                  onChange={(e) => {
                    setCopaymentOption(e.target.value);

                    allianzCareSingaporeSpecific['outpatient_co_payment'] = e.target.value;
                    setAllianzCareSingaporeSpecific({ ...allianzCareSingaporeSpecific });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    copaymentOptions && copaymentOptions.map(copaymentOption => (
                      <MenuItem key={copaymentOption} value={copaymentOption}>{copaymentOption}</MenuItem>
                    ))
                  }

                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={400} sx={{ width: 200 }}>Family</Typography>
                <Select
                  size='small'
                  value={family}
                  onChange={(e) => {
                    setFamily(e.target.value);

                    allianzCareSingaporeSpecific['family'] = e.target.value;
                    setAllianzCareSingaporeSpecific({ ...allianzCareSingaporeSpecific });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    ["Yes", "No"].map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))
                  }

                </Select>
              </Box>


              <Box sx={{ display: 'flex', alignItems: 'start', mt: 5 }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Area Of Coverage</Typography>
                <Select
                  size='small'
                  value={specificAreaOfCoverage[id]}
                  onChange={(e) => {
                    specificAreaOfCoverage[id] = e.target.value;
                    setSpecificAreaOfCoverage({ ...specificAreaOfCoverage });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {/* <MenuItem value={'parent'} sx={{ color: colors.blue[500] }}>{areaOfCoverage}</MenuItem> */}
                  {
                    specific_area_of_coverages && specific_area_of_coverages.map(area_of_coverage => (
                      <MenuItem key={area_of_coverage} value={area_of_coverage}>{area_of_coverage}</MenuItem>
                    ))
                  }

                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Currency</Typography>
                <Select
                  size='small'
                  value={specificCurrency[id]}
                  onChange={(e) => {
                    specificCurrency[id] = e.target.value;
                    setSpecificCurrency({ ...specificCurrency });

                    setDeductibles(prev => {
                      return {
                        ...prev,
                        [id]: getSafe(() => appConfig.content.deductibles.allianz_care_singapore[specificCurrency[id]][0]),
                      };
                    });


                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    ["SGD", "USD"].map(currency => (
                      <MenuItem key={currency} value={currency}>{currency}</MenuItem>
                    ))
                  }

                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Payment Frequency</Typography>
                <Select
                  size='small'
                  value={specificPaymentFrequency[id]}
                  onChange={(e) => {
                    specificPaymentFrequency[id] = e.target.value;
                    setSpecificPaymentFrequency({ ...specificPaymentFrequency });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    ["Annually", "Semi Annually", "Quarterly", "Monthly"].map(payment_frequency => (
                      <MenuItem key={payment_frequency} value={payment_frequency}>{payment_frequency}</MenuItem>
                    ))
                  }

                </Select>
              </Box>


            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={18} fontWeight={400} sx={{ visibility: 'hidden' }}>Discounts</Typography>
              <Box sx={{ display: 'flex', alignItems: 'start', mt: 1 }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Renewal/Further Discount</Typography>
                <PercentInput
                  sx={{ width: '80px', textAlign: 'end' }}
                  inputProps={{}}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  value={discounts[id]}
                  onCommit={(value) => setDiscounts(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Renewal/Further Loading</Typography>
                <PercentInput
                  sx={{ width: '80px', textAlign: 'end' }}
                  inputProps={{}}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  value={loadings[id]}
                  onCommit={(value) => setLoadings(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
            </Box>
          </Box>

          {
            validationErrorMessage &&
            <ul style={{ color: colors.red['A700'] }}>
              <li>{validationErrorMessage}</li>
            </ul>
          }


          <div style={{ display: 'flex', justifyContent: 'end', width: '100%' }}>
            <Button
              size='small'
              sx={{ mb: 1 }}
              onClick={() => {
                var node = document.getElementById('table-first-choice-rates');

                node.style = 'width: auto;';

                domtoimage.toBlob(node).then(function (blob) {
                  // Create ClipboardItem with blob and its type, and add to an array
                  const data = [new ClipboardItem({ [blob.type]: blob })];
                  // Write the data to the clipboard
                  navigator.clipboard.write(data);

                  setShowImageCopy(true);

                  node.style = 'width: 100%;';
                })
                  .catch(function (error) {
                    console.error('oops, something went wrong!', error);
                    node.style = 'width: 100%;';
                  });
              }}
              endIcon={<ContentCopyIcon />}
            >Copy Image</Button>
          </div>

          <TableContainer component={Paper}>
            <Table id='table-first-choice-rates' size='small' sx={{ minWidth: 650 }}>
              <TableHead sx={{ background: '#003781', color: '#fff' }}>
                <TableRow>
                  <TableCell sx={{ width: 345, color: '#fff', fontWeight: 600 }}>Plan</TableCell>
                  {
                    clients.map((client, idx) => {
                      if (client.full_name.trim() === '') {
                        return <TableCell sx={{ width: 200, fontWeight: 600, color: '#fff' }} key={idx}>_</TableCell>
                      }

                      return <TableCell sx={{ width: 200, color: '#fff', fontWeight: 600 }} key={idx}><div>{client.full_name} ({client.age})</div><div>{client.country_of_residence}</div></TableCell>
                    })
                  }
                  <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Subtotal</TableCell>
                </TableRow>

              </TableHead>
              <TableBody>
                <TableRow sx={{ backgroundColor: '#ffffff' }}>
                  <TableCell sx={{ border: '2px solid #fff', fontWeight: 600, background: '#bdbdbd' }}>Family Discount</TableCell>
                  {
                    clients.map((client, idx) => {
                      return <TableCell sx={{ border: '2px solid #fff', background: '#bdbdbd' }} key={idx}> {loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : checkIfFamilyDiscountPerClient(idx)}</TableCell>
                    })
                  }
                  <TableCell sx={{ border: '2px solid #fff', background: '#bdbdbd' }}>{loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : ''}</TableCell>
                </TableRow>
                <TableRatesRow
                  planId='allianz_care_singapore_inpatient'
                  planName='inpatient'
                  planLabel={<span><span style={{ fontWeight: 700 }}>Inpatient</span> <span style={{ fontSize: 14 }}>({inpatientOption})</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  bgColor='#81d4fa'
                  loading={loadingStatus[id]}
                  rates={response ? computeAllianzCareSingaporeRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={true}
                  fractionDigits={0}
                />
                <TableRatesRow
                  planId='allianz_care_singapore_outpatient'
                  planName='outpatient'
                  planLabel={<span><span style={{ fontWeight: 700 }}>Outpatient</span> <span style={{ fontSize: 14 }}>({outpatientOption})</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  bgColor='#81d4fa'
                  loading={loadingStatus[id]}
                  rates={response ? computeAllianzCareSingaporeRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={outpatientOption !== 'No out-patient'}
                  fractionDigits={0}
                />
                <TableRatesRow
                  planId='allianz_care_singapore_maternity'
                  planName='maternity'
                  planLabel={<span><span style={{ fontWeight: 700 }}>Maternity</span> <span style={{ fontSize: 14 }}>({maternityOption})</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  bgColor='#81d4fa'
                  loading={loadingStatus[id]}
                  rates={response ? computeAllianzCareSingaporeRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={maternityOption !== 'No Maternity'}
                  fractionDigits={0}
                />
                <TableRatesRow
                  planId='allianz_care_singapore_dental'
                  planName='dental'
                  planLabel={<span><span style={{ fontWeight: 700 }}>Dental</span> <span style={{ fontSize: 14 }}>({dentalOption})</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  bgColor='#81d4fa'
                  loading={loadingStatus[id]}
                  rates={response ? computeAllianzCareSingaporeRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={dentalOption !== 'No Dental'}
                  fractionDigits={0}
                />
                <TableRatesRow
                  planId='allianz_care_singapore_repatriation'
                  planName='repatriation'
                  planLabel={<span><span style={{ fontWeight: 700 }}>Repatriation</span> <span style={{ fontSize: 14 }}>({repatriationOption})</span></span>}
                  clients={clients}
                  selectedPlans={selectedPlans}
                  bgColor='#81d4fa'
                  loading={loadingStatus[id]}
                  rates={response ? computeAllianzCareSingaporeRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={repatriationOption !== 'No Repatriation'}
                  fractionDigits={0}
                />

                <TableRow sx={{ backgroundColor: '#ffffff' }}>
                  <TableCell sx={{ border: '2px solid #fff', fontWeight: 600, background: '#bdbdbd' }}>Total Premium Incl. GST {gst * 100}%</TableCell>
                  {
                    clients.map((client, idx) => {
                      return <TableCell sx={{ border: '2px solid #fff', background: '#bdbdbd' }} key={idx}> {loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremiumPerClient(idx))}</TableCell>
                    })
                  }
                  <TableCell sx={{ border: '2px solid #fff', background: '#bdbdbd' }}>{loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremium())}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

          </TableContainer>


        </Box>


      </Paper>

    </Box>
  );
});

export default AllianzCareSingapore_Rates;